import React from "react";

import { cn } from "@/lib/utils";

import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

//

type Sizes = "h1" | "h2" | "h3" | "h4" | "p" | "base" | "sm";

//

const tagsBySizeMap: Record<Sizes, keyof HTMLElementTagNameMap> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  p: "p",
  base: "p",
  sm: "p",
};

const classBySizeMap: Record<Sizes, string> = {
  h1: "text-3xl font-semibold tracking-tight leading-tight",
  h2: "text-2xl font-semibold tracking-tight leading-tight",
  h3: "text-xl font-semibold tracking-tight leading-tight",
  h4: "text-lg font-semibold tracking-tight leading-tight",
  p: "text-base",
  base: "text-sm",
  sm: "text-xs",
};

const boldBySizeMap: Record<Sizes, string> = {
  h1: "font-bold",
  h2: "font-bold",
  h3: "font-bold",
  h4: "font-bold",
  p: "font-semibold",
  base: "font-semibold",
  sm: "font-medium",
};

//

export interface TypographyProps {
  children?: React.ReactNode;
  className?: string;
  muted?: boolean;
  size?: Sizes;
  bold?: boolean;
  text?: React.ReactNode;
  sm?: boolean;
  p?: boolean;
  h4?: boolean;
  tooltip?: string;
}

//

export const Typography: React.FC<TypographyProps> = ({
  children,
  className,
  size = "base",
  muted,
  bold,
  text = children,
  sm,
  p,
  h4,
  tooltip,
}) => {
  if (p) size = "p";
  if (h4) size = "h4";
  if (sm) size = "sm";

  const tag = tagsBySizeMap[size];
  const classBySize = classBySizeMap[size];
  const boldClass = bold ? boldBySizeMap[size] : "";
  const mutedClass = muted ? "text-muted-foreground" : "";

  const element = React.createElement(
    tag,
    {
      className: cn(classBySize, boldClass, mutedClass, className),
    },
    text,
  );

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{element}</TooltipTrigger>
        {tooltip && <TooltipContent>{tooltip}</TooltipContent>}
      </Tooltip>
    );
  }

  return element;
};
